import type { FontToken, FontProperties } from '../../../typography/types';
import type { StyleResolverContext, CssFragment } from './index';
import { DEFAULT_FONT } from '../../../constants/text';

// ============================================================================
// Types
// ============================================================================

export interface FontProps {
  font?: FontToken;
}

export interface TextDecorationProps {
  strikeThrough?: boolean;
}

// ============================================================================
// Resolution helpers
// ============================================================================

const resolveFontCss = (fontProperties: FontProperties | undefined): CssFragment[] => {
  if (!fontProperties) return [];

  return [
    fontProperties.fontFamily ? `font-family: ${fontProperties.fontFamily};` : null,
    fontProperties.size ? `font-size: ${fontProperties.size}px;` : null,
    fontProperties.weight ? `font-weight: ${fontProperties.weight};` : null,
    fontProperties.lineHeight ? `line-height: ${fontProperties.lineHeight}px;` : null,
    fontProperties.letterSpacing ? `letter-spacing: ${fontProperties.letterSpacing};` : null,
  ];
};

const resolveTextDecorationCss = (props: TextDecorationProps): CssFragment =>
  props.strikeThrough ? 'text-decoration: line-through;' : null;

// ============================================================================
// Pickers
// ============================================================================

const pickFontProps = <T extends Partial<FontProps>>(props: T): FontProps => {
  const { font } = props;
  return { font };
};

const pickTextDecorationProps = <T extends Partial<TextDecorationProps>>(props: T): TextDecorationProps => {
  const { strikeThrough } = props;
  return { strikeThrough };
};

// ============================================================================
// Style resolvers
// ============================================================================

export const resolveFontStyleCss = (props: Record<string, unknown>, theme: StyleResolverContext): CssFragment[] => {
  const { font = DEFAULT_FONT } = pickFontProps(props as Partial<FontProps>);
  return resolveFontCss(theme.getFont(font));
};

export const resolveTextDecorationStyleCss = (
  props: Record<string, unknown>,
  _theme: StyleResolverContext,
): CssFragment[] => [resolveTextDecorationCss(pickTextDecorationProps(props as Partial<TextDecorationProps>))];
